// web/src/components/enroll/NewSecretWizard.tsx — the multi-step new-secret wizard
// (wireframe 03; M2 seal driver). Steps: ① Identity ② Material ③ Quorum ④ Review.
// On "Convene & seal" it hands the collected draft up to the caller, which opens
// an enrollment session and routes to the initiator lobby (/enroll) where the
// admitted members' keys are gathered and the secret is split client-side.
//
// Plaintext invariant (R11): the material entered here stays in component state
// and is handed to the lobby in-memory only — it is never sent to the server.
import React, { useState } from 'react';
import { SENSITIVITY_LEVELS, type Sensitivity } from '../../../../shared/types';
import Modal from '../Modal';
import { FeedbackMessage } from '../ui';
import { SENSITIVITY_META } from '../parts';
import { SecretGenerator } from './SecretGenerator';

/** The plaintext-bearing draft the wizard produces. Held in-memory only (R11). */
export interface SecretDraft {
  name: string;
  description: string;
  sensitivity: Sensitivity;
  material: string;
  thresholdK: number;
  totalN: number;
  timeoutSeconds: number;
}

export interface NewSecretWizardProps {
  /** Called on finish. Opens the session + routes to the lobby. Plaintext stays here. */
  onConvene: (draft: SecretDraft) => Promise<void>;
  onClose: () => void;
}

const WINDOWS: ReadonlyArray<{ label: string; seconds: number }> = [
  { label: '15 minutes', seconds: 15 * 60 },
  { label: '30 minutes', seconds: 30 * 60 },
  { label: '1 hour', seconds: 60 * 60 },
  { label: '4 hours', seconds: 4 * 60 * 60 },
];

const STEP_LABELS = ['Identity', 'Material', 'Quorum', 'Review'] as const;

export function NewSecretWizard({ onConvene, onClose }: NewSecretWizardProps): React.JSX.Element {
  const [step, setStep] = useState(0);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [sensitivity, setSensitivity] = useState<Sensitivity>('high');
  const [material, setMaterial] = useState('');
  const [thresholdK, setThresholdK] = useState(3);
  const [totalN, setTotalN] = useState(5);
  const [timeoutSeconds, setTimeoutSeconds] = useState(30 * 60);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  function stepValid(i: number): boolean {
    if (i === 0) return name.trim().length > 0;
    if (i === 1) return material.trim().length > 0;
    if (i === 2) return totalN >= 2 && thresholdK >= 2 && thresholdK <= totalN;
    return true;
  }

  function next(): void {
    if (!stepValid(step)) {
      setError(stepError(step));
      return;
    }
    setError(null);
    setStep((s) => Math.min(s + 1, STEP_LABELS.length - 1));
  }
  function back(): void {
    setError(null);
    setStep((s) => Math.max(s - 1, 0));
  }

  function stepError(i: number): string {
    if (i === 0) return 'A name is required.';
    if (i === 1) return 'Enter the secret material to protect.';
    if (i === 2) return 'Quorum must be 2 of 2 up to K of N, with K no greater than N.';
    return '';
  }

  async function convene(): Promise<void> {
    setBusy(true);
    setError(null);
    try {
      await onConvene({
        name: name.trim(),
        description: description.trim(),
        sensitivity,
        material: material.trim(),
        thresholdK,
        totalN,
        timeoutSeconds,
      });
      // The caller routes away on success; closing is the caller's responsibility.
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
      setBusy(false);
    }
  }

  const isLast = step === STEP_LABELS.length - 1;
  const noRedundancy = thresholdK === totalN;

  return (
    <Modal
      title="New secret"
      subtitle={`Step ${step + 1} of ${STEP_LABELS.length} · ${STEP_LABELS[step]}`}
      maxWidth={560}
      dismissable={!busy}
      onClose={onClose}
      footer={
        <>
          {step > 0 ? (
            <button type="button" className="btn btn-ghost" onClick={back} disabled={busy}>
              Back
            </button>
          ) : (
            <button type="button" className="btn btn-ghost" onClick={onClose} disabled={busy}>
              Cancel
            </button>
          )}
          {isLast ? (
            <button type="button" className="btn btn-primary" onClick={convene} disabled={busy}>
              {busy ? <span className="spinner" aria-hidden="true" /> : 'Convene & seal'}
            </button>
          ) : (
            <button
              type="button"
              className="btn btn-primary"
              onClick={next}
              disabled={busy || !stepValid(step)}
            >
              Next
            </button>
          )}
        </>
      }
    >
      <ol className="bgSteps" aria-label="Wizard progress" style={{ marginBottom: '1rem' }}>
        {STEP_LABELS.map((label, i) => (
          <li key={label} style={{ display: 'flex', alignItems: 'center', gap: '0.4rem' }}>
            <span
              className={`bgStepDot ${i === step ? 'bgStepOn' : ''} ${i < step ? 'bgStepDone' : ''}`}
              aria-hidden="true"
            />
            <span aria-current={i === step ? 'step' : undefined}>{label}</span>
          </li>
        ))}
      </ol>

      {step === 0 && (
        <div style={{ display: 'grid', gap: '0.95rem' }}>
          <div>
            <label className="field-label" htmlFor="ns-name">Name</label>
            <input
              id="ns-name"
              className="input"
              autoFocus
              value={name}
              onChange={(e) => { setName(e.target.value); if (error) setError(null); }}
              placeholder="Production DB master"
            />
          </div>
          <div>
            <label className="field-label" htmlFor="ns-desc">Description</label>
            <input
              id="ns-desc"
              className="input"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Master credential for prod Postgres"
            />
          </div>
          <div>
            <span className="field-label">Sensitivity</span>
            <div className="bgChips" role="group" aria-label="Sensitivity">
              {SENSITIVITY_LEVELS.map((s) => {
                const active = sensitivity === s;
                return (
                  <button
                    key={s}
                    type="button"
                    className={`bgChip ${active ? 'bgChipActive' : ''}`}
                    aria-pressed={active}
                    onClick={() => setSensitivity(s)}
                    style={active ? undefined : { color: SENSITIVITY_META[s].colorVar }}
                  >
                    {SENSITIVITY_META[s].label}
                  </button>
                );
              })}
            </div>
          </div>
        </div>
      )}

      {step === 1 && (
        <SecretGenerator
          material={material}
          onMaterialChange={(value) => { setMaterial(value); if (error) setError(null); }}
        />
      )}

      {step === 2 && (
        <div style={{ display: 'grid', gap: '0.95rem' }}>
          <div style={{ display: 'flex', gap: '1rem', flexWrap: 'wrap' }}>
            <div>
              <label className="field-label" htmlFor="ns-k">Threshold K</label>
              <input
                id="ns-k"
                className="input"
                type="number"
                min={2}
                max={totalN}
                value={thresholdK}
                onChange={(e) => setThresholdK(Number(e.target.value))}
                style={{ width: '6rem' }}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="ns-n">Members N</label>
              <input
                id="ns-n"
                className="input"
                type="number"
                min={2}
                max={255}
                value={totalN}
                onChange={(e) => setTotalN(Number(e.target.value))}
                style={{ width: '6rem' }}
              />
            </div>
            <div>
              <label className="field-label" htmlFor="ns-window">Window</label>
              <select
                id="ns-window"
                className="input"
                value={timeoutSeconds}
                onChange={(e) => setTimeoutSeconds(Number(e.target.value))}
              >
                {WINDOWS.map((w) => (
                  <option key={w.seconds} value={w.seconds}>{w.label}</option>
                ))}
              </select>
            </div>
          </div>
          <p className="bgQuorumPreview">
            Requires {thresholdK} of {totalN} · margin {Math.max(totalN - thresholdK, 0)}
          </p>
          {noRedundancy && (
            <FeedbackMessage ok={false} msg="No redundancy: losing one member blocks every unseal." />
          )}
          <p className="bgShieldNote" style={{ marginTop: 0 }}>
            Your secret is split into {totalN} shards. Any {thresholdK} of them can rebuild it; fewer cannot.
          </p>
        </div>
      )}

      {step === 3 && (
        <div style={{ display: 'grid', gap: '0.75rem' }}>
          <div style={{ fontSize: 'var(--size-h3)', color: 'var(--color-text)' }}>
            {name || 'Untitled'} · {SENSITIVITY_META[sensitivity].label}
          </div>
          <dl style={{ display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 1rem', margin: 0 }}>
            <dt className="field-label">Material</dt>
            <dd style={{ margin: 0, color: 'var(--color-text2)' }}>provided ({material.length} chars)</dd>
            <dt className="field-label">Quorum</dt>
            <dd style={{ margin: 0, color: 'var(--color-text2)' }}>{thresholdK} of {totalN}</dd>
            <dt className="field-label">Window</dt>
            <dd style={{ margin: 0, color: 'var(--color-text2)' }}>
              {WINDOWS.find((w) => w.seconds === timeoutSeconds)?.label ?? `${timeoutSeconds}s`}
            </dd>
          </dl>
          <p className="bgShieldNote" style={{ marginTop: 0 }}>
            Next: you convene a session; members join and verify; then the secret is split
            client-side and one encrypted shard is delivered to each.
          </p>
        </div>
      )}

      {error && <div style={{ marginTop: '0.9rem' }}><FeedbackMessage ok={false} msg={error} /></div>}
    </Modal>
  );
}

export default NewSecretWizard;
